import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, CheckCircle, Truck, Clock } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function TrackOrderPage() {
  const [trackingId, setTrackingId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.get(`/orders/track/${trackingId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error('Order not found');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'processing', 'shipped', 'delivered'];
    return steps.indexOf(status) + 1;
  };

  return (
    <div className="min-h-screen py-12" data-testid="track-order-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a3a5c] mb-4" data-testid="track-title">
            Track Your Order
          </h1>
          <p className="text-gray-600">Enter your tracking ID to see order status</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleTrack}
          className="bg-white p-8 rounded-lg border border-gray-200 mb-8"
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder="Enter tracking ID (e.g., TRK123456789)"
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                required
                data-testid="tracking-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-[#1a3a5c] text-white px-8 py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 flex items-center space-x-2 disabled:opacity-50"
              data-testid="track-button"
            >
              <Search className="h-5 w-5" />
              <span>{loading ? 'Searching...' : 'Track'}</span>
            </button>
          </div>
        </motion.form>

        {order && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 rounded-lg border border-gray-200"
            data-testid="order-details"
          >
            <div className="border-b border-gray-200 pb-6 mb-6">
              <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-2">
                Order #{order.tracking_id}
              </h2>
              <p className="text-sm text-gray-600">
                Placed on {new Date(order.created_at).toLocaleDateString()}
              </p>
            </div>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="text-center flex-1">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                    getStatusStep(order.order_status) >= 1 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                  }`}>
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-medium">Order Placed</p>
                </div>
                <div className={`flex-1 h-1 ${
                  getStatusStep(order.order_status) >= 2 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                }`}></div>
                <div className="text-center flex-1">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                    getStatusStep(order.order_status) >= 2 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                  }`}>
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-medium">Processing</p>
                </div>
                <div className={`flex-1 h-1 ${
                  getStatusStep(order.order_status) >= 3 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                }`}></div>
                <div className="text-center flex-1">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                    getStatusStep(order.order_status) >= 3 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                  }`}>
                    <Truck className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-medium">Shipped</p>
                </div>
                <div className={`flex-1 h-1 ${
                  getStatusStep(order.order_status) >= 4 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                }`}></div>
                <div className="text-center flex-1">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                    getStatusStep(order.order_status) >= 4 ? 'bg-[#1a3a5c]' : 'bg-gray-300'
                  }`}>
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-xs font-medium">Delivered</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-3">Order Items</h3>
              <div className="space-y-2">
                {order.items.map((item, index) => (
                  <div key={`${item.product_id}-${index}`} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.product_name} x{item.quantity}
                    </span>
                    <span className="font-medium">₹{item.total_price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between font-bold text-lg">
                <span className="text-[#1a3a5c]">Total Amount</span>
                <span className="text-[#1a3a5c]">₹{order.total_amount.toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Payment Status: <span className="capitalize font-medium">{order.payment_status}</span>
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}